import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';
import { AreaDto, CreateAreaDto, UpdateAreaDto } from '../../services/area-management.service';

export interface AreaEditorDialogData {
  area?: AreaDto;
}

export interface AreaEditorDialogResult {
  area: CreateAreaDto | UpdateAreaDto;
}

@Component({
  selector: 'app-area-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ModalShellComponent,
  ],
  templateUrl: './area-editor.component.html',
  styleUrl: './area-editor.component.scss',
})
export class AreaEditorComponent {
  readonly modalConfig: ModalShellConfig;
  readonly form: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<AreaEditorComponent, AreaEditorDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: AreaEditorDialogData,
  ) {
    this.modalConfig = { title: data.area ? 'Editar área' : 'Nueva área', size: 'md', layout: 'form' };
    this.form = this.fb.group({
      name: [data.area?.name ?? '', [Validators.required, Validators.minLength(3), Validators.maxLength(150)]],
      isActive: [data.area?.isActive ?? true],
    });
  }

  get isEditMode(): boolean {
    return !!this.data.area;
  }

  saveArea(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.dialogRef.close({
      area: {
        name: String(value.name).trim(),
        isActive: value.isActive,
        ...(this.data.area ? { sortOrder: this.data.area.sortOrder } : {}),
      },
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
